import type { NativeMessageEntity, TextEdit } from "../types"

export type EntityRangeResult =
  | { ok: true; entities: NativeMessageEntity[] }
  | { ok: false; reason: string; entityIndex?: number }

// Label edits preserve a text_link's destination. User mentions, custom emoji,
// auto-detected tokens and unknown entity kinds are deliberately opaque.
const resizableTypes = new Set<string>([
  "bold",
  "italic",
  "underline",
  "strikethrough",
  "spoiler",
  "code",
  "pre",
  "text_link",
  "blockquote",
  "expandable_blockquote",
])

/** The mapper can check document bounds when the caller supplies textLength. */
export function getTextEditError(
  edit: TextEdit,
  textLength?: number,
): string | undefined {
  if (
    !Number.isSafeInteger(edit.from) ||
    !Number.isSafeInteger(edit.to) ||
    edit.from < 0 ||
    edit.to < edit.from ||
    typeof edit.text !== "string" ||
    !Number.isSafeInteger(edit.from + edit.text.length)
  ) {
    return "Invalid UTF-16 text edit range"
  }
  if (
    textLength !== undefined &&
    (!Number.isSafeInteger(textLength) ||
      textLength < 0 ||
      edit.to > textLength ||
      !Number.isSafeInteger(
        textLength - (edit.to - edit.from) + edit.text.length,
      ))
  ) {
    return "Text edit is outside the document range"
  }
}

function validRange(entity: NativeMessageEntity, textLength?: number): boolean {
  const end = entity.offset + entity.length
  return (
    Number.isSafeInteger(entity.offset) &&
    entity.offset >= 0 &&
    Number.isSafeInteger(entity.length) &&
    entity.length > 0 &&
    Number.isSafeInteger(end) &&
    (textLength === undefined || end <= textLength)
  )
}

/** Geometry check only; entity-specific nesting restrictions are separate. */
function findCrossingRange(
  entities: readonly NativeMessageEntity[],
): number | undefined {
  const ordered = entities
    .map((entity, index) => ({ entity, index }))
    .sort(
      (a, b) =>
        a.entity.offset - b.entity.offset || b.entity.length - a.entity.length,
    )
  const ends: number[] = []
  for (const { entity, index } of ordered) {
    while (ends.length && ends[ends.length - 1] <= entity.offset) ends.pop()
    const end = entity.offset + entity.length
    if (ends.length && end > ends[ends.length - 1]) return index
    ends.push(end)
  }
}

/**
 * Telegram permits styles around links, but links cannot contain each other.
 * text_link is the only resizable link kind; other link-like kinds are opaque.
 */
function findOverlappingLink(
  entities: readonly NativeMessageEntity[],
): number | undefined {
  const links = entities
    .map((entity, index) => ({ entity, index }))
    .filter(({ entity }) => entity.type === "text_link")
    .sort((a, b) => a.entity.offset - b.entity.offset)
  for (let index = 1; index < links.length; index++) {
    const previous = links[index - 1].entity
    if (links[index].entity.offset < previous.offset + previous.length) {
      return links[index].index
    }
  }
}

/**
 * Insertion at an annotation's start is before it; insertion at its end is
 * after it. Interior edits resize supported formatting, including replacement
 * text. Fully deleted formatting disappears. Opaque intersections always fail,
 * even for a whole-range deletion, until removeAnnotation is explicitly used.
 * Only offset/length are replaced; nested metadata is never reconstructed.
 */
export function mapEntityRanges(
  entities: readonly NativeMessageEntity[],
  edit: TextEdit,
  textLength?: number,
): EntityRangeResult {
  const editError = getTextEditError(edit, textLength)
  if (editError) return { ok: false, reason: editError }
  const invalidIndex = entities.findIndex(
    (entity) => !validRange(entity, textLength),
  )
  if (invalidIndex !== -1) {
    return {
      ok: false,
      reason: `Invalid entity range at index ${invalidIndex}`,
      entityIndex: invalidIndex,
    }
  }
  const crossingIndex = findCrossingRange(entities)
  if (crossingIndex !== undefined) {
    return {
      ok: false,
      reason: "Crossing annotation ranges in the source",
      entityIndex: crossingIndex,
    }
  }
  const delta = edit.text.length - (edit.to - edit.from)
  const mapped: NativeMessageEntity[] = []
  const originalIndices: number[] = []

  for (let entityIndex = 0; entityIndex < entities.length; entityIndex++) {
    const entity = entities[entityIndex]
    const start = entity.offset
    const end = start + entity.length
    let next = entity
    if (edit.from === edit.to && edit.text.length === 0) {
      // A no-op is safe even inside an opaque annotation.
    } else if (edit.to <= start) {
      next = { ...entity, offset: start + delta }
    } else if (edit.from >= end) {
      // The range is entirely before the edit.
    } else {
      if (!resizableTypes.has(entity.type)) {
        return {
          ok: false,
          reason: `Edit intersects opaque annotation ${entityIndex} (${entity.type}); remove it explicitly first`,
          entityIndex,
        }
      }
      const offset = Math.min(start, edit.from)
      const mappedEnd =
        edit.to >= end ? edit.from + edit.text.length : end + delta
      const length = mappedEnd - offset
      if (length === 0) continue
      next = { ...entity, offset, length }
    }
    if (!validRange(next)) {
      return {
        ok: false,
        reason: `Mapped entity range is invalid at index ${entityIndex}`,
        entityIndex,
      }
    }
    mapped.push(next)
    originalIndices.push(entityIndex)
  }
  const mappedCrossingIndex = findCrossingRange(mapped)
  if (mappedCrossingIndex !== undefined) {
    return {
      ok: false,
      reason: "Edit would create crossing annotation ranges",
      entityIndex: originalIndices[mappedCrossingIndex],
    }
  }
  const overlappingLinkIndex = findOverlappingLink(mapped)
  if (overlappingLinkIndex !== undefined) {
    return {
      ok: false,
      reason: "Edit would create overlapping link annotations",
      entityIndex: originalIndices[overlappingLinkIndex],
    }
  }
  return { ok: true, entities: mapped }
}

/** Remove only the selected annotation, not its text or any nested metadata. */
export function removeAnnotation(
  entities: readonly NativeMessageEntity[],
  entityIndex: number,
): EntityRangeResult {
  if (
    !Number.isSafeInteger(entityIndex) ||
    entityIndex < 0 ||
    entityIndex >= entities.length
  ) {
    return { ok: false, reason: "Invalid annotation index", entityIndex }
  }
  return {
    ok: true,
    entities: entities.filter((_, index) => index !== entityIndex),
  }
}
